import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { GetMyPrincipal, GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { Flex, Link, Button, ConfirmModal } from 'nallian-shared-ui/lib/components';
import { Table, Params, Actions, Menu, Merge } from 'nallian-table-builder-ui';
import {
  INFINITE_SCROLL,
  PRINCIPALS_RESOURCE,
  PRINCIPAL_ACTIONS,
  ACTIONS,
  PRINCIPAL_STATES,
} from '../../constants';
import NewPrincipal from './NewPrincipal';
import EditPrincipal from './EditPrincipal';
import PrincipalState from './table/PrincipalState';
import PrincipalStateFilter from './table/PrincipalStateFilter';
import ConditionalMenu from './table/ConditionalMenu';
import activatePrincipalService from '../../services/principals/activatePrincipalService';
import apiRoutes from '../../../../app/api/routes';
import { isSystemOrganisation } from '../../../../app/config/systemOrgs';
import PrincipalFilters from './filter/PrincipalFilters';
import ImpersonationRequestModal from '../../../principals/components/impersonation/ImpersonationRequestModal';

export const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const PrincipalsOverview = ({
  forbidManageBecauseSystemOrg,
  allowDeleted,
  organisationId,
  urlRoot,
  isOrgAdmin,
  dispatchActivatePrincipalPost,
  activatePrincipalFetch,
}) => {
  const { t } = useTranslation();
  const { action, actionId } = useParams();

  const principal = useSelector(GetMyPrincipal);
  const rights = useSelector(GetRights);
  const canCreateImpersonation = hasRight(rights, 'impersonation-requests/create');

  const isSystemOrg = useMemo(() => isSystemOrganisation(organisationId), [organisationId]);
  const iAmSystemOrg = useMemo(() => isSystemOrganisation(principal?.organisation?.id), [
    principal,
  ]);

  const filtered = useMemo(
    () => ({
      defaults: { organisationId },
    }),
    [organisationId]
  );

  const RESOURCE = `${PRINCIPALS_RESOURCE}_${organisationId}`;

  const [activatePrincipalItem, setActivatePrincipalItem] = useState(null);

  const handleActivatePrincipalClick = useCallback((_, item) => setActivatePrincipalItem(item), [
    setActivatePrincipalItem,
  ]);

  const handleActivatePrincipalConfirm = useCallback(() => {
    dispatchActivatePrincipalPost(
      activatePrincipalItem?.id,
      activatePrincipalItem?.organisation?.id,
      () => setActivatePrincipalItem(null),
      t
    );
  }, [activatePrincipalItem, dispatchActivatePrincipalPost, setActivatePrincipalItem, t]);

  const handleClearActivePrincipalId = useCallback(() => setActivatePrincipalItem(null), [
    setActivatePrincipalItem,
  ]);

  const [impersonationTarget, setImpersonationTarget] = useState(null);
  const handleRequestImpersonation = useCallback((_, item) => setImpersonationTarget(item), []);
  const handleCloseImpersonationModal = useCallback(() => setImpersonationTarget(null), []);

  return (
    <>
      {!forbidManageBecauseSystemOrg && (
        <HasSomeRights rights={PRINCIPAL_RIGHTS}>
          <NewPrincipal
            isOrgAdmin={isOrgAdmin}
            urlRoot={urlRoot}
            organisationId={organisationId}
            open={organisationId && action === ACTIONS.NEW}
          />
          <Flex>
            <Link to={`${urlRoot}/users/${ACTIONS.NEW}`}>
              <Button btnprimary icon="add" content={t('addUser')} />
            </Link>
          </Flex>
        </HasSomeRights>
      )}
      <EditPrincipal
        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
        urlRoot={urlRoot}
        organisationId={organisationId}
        isSystemOrg={isSystemOrg}
        open={organisationId && actionId && action === PRINCIPAL_ACTIONS.EDIT}
        id={actionId}
      />
      <PrincipalFilters resource={RESOURCE} organisationId={organisationId} />
      <Table
        infiniteScroll={INFINITE_SCROLL}
        resource={RESOURCE}
        url={apiRoutes.principals()}
        noResultsMessage={t('noUsersFound')}
        pageSize={50}
        filtered={filtered}
        sorted
        noUserSettings
        resizable
      >
        <Actions.Link
          width="2.5rem"
          align="center"
          icon="eye"
          url={`${urlRoot}/users/${PRINCIPAL_ACTIONS.DETAILS}/:id`}
        />
        <Params.String name="fullName" width="16rem" minWidth="16rem" grow />
        <Params.String
          name="primaryEmailAddress"
          label="email"
          width="16rem"
          minWidth="16rem"
          grow
        />
        <Params.Custom
          filter={PrincipalStateFilter}
          content={PrincipalState}
          withoutDeleted={!allowDeleted && !iAmSystemOrg}
          label="state"
          name="activeState"
          width="16rem"
          grow
          noSort
        />
        {!forbidManageBecauseSystemOrg && (
          <Merge
            width="3.5rem"
            align="center"
            as={ConditionalMenu}
            hasSomeRights={PRINCIPAL_RIGHTS}
          >
            <Menu width="3.5rem" align="center">
              <Actions.Link
                label="edit"
                icon="edit"
                url={`${urlRoot}/users/${PRINCIPAL_ACTIONS.EDIT}/:id`}
              />
              <Actions.Button
                icon="check"
                label="activate"
                onClick={handleActivatePrincipalClick}
                state={PRINCIPAL_STATES.PENDING}
              />
              <Actions.Button
                icon="user"
                label={t('requestImpersonation')}
                onClick={handleRequestImpersonation}
                state={PRINCIPAL_STATES.ACTIVE}
                hidden={!canCreateImpersonation}
              />
              <Actions.Delete
                url={`${apiRoutes.principals()}/:id`}
                confirmMessage={(_, item) =>
                  t('areYouSureRemovePrincipal', {
                    name: item?.fullName || item?.primaryEmailAddress,
                    email: item?.primaryEmailAddress,
                  })
                }
                onSuccessMessage={t('principalRemoved')}
              />
            </Menu>
          </Merge>
        )}
      </Table>
      <ConfirmModal
        open={!!activatePrincipalItem}
        header={t('areYouSureActivatePrincipal', {
          name: activatePrincipalItem?.fullName || activatePrincipalItem?.primaryEmailAddress,
        })}
        onConfirm={handleActivatePrincipalConfirm}
        onCancel={handleClearActivePrincipalId}
        cancelContent={t('cancel')}
        confirmContent={t('confirm')}
        confirmLoading={activatePrincipalFetch.pending}
      />
      <ImpersonationRequestModal
        open={!!impersonationTarget}
        targetPrincipal={impersonationTarget}
        onClose={handleCloseImpersonationModal}
      />
    </>
  );
};

PrincipalsOverview.propTypes = {
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool,
  isOrgAdmin: PropTypes.bool.isRequired,
  allowDeleted: PropTypes.bool,
  dispatchActivatePrincipalPost: PropTypes.func.isRequired,
  activatePrincipalFetch: PropTypes.object.isRequired,
};

PrincipalsOverview.defaultProps = { forbidManageBecauseSystemOrg: false, allowDeleted: false };

export default activatePrincipalService(PrincipalsOverview);
